import type { Cart_jsonld_read, Cart_jsonld_write, CartItemRequest } from '@/features/cart/cart-post-type';

import React from 'react';

import { centsToPriceString } from '@/core/features/price/price';
import { useQuery } from '@/core/features/react-query/react-query-service';
import { DEFAULT_QUERY_STALE_TIME } from '@/core/features/request/request-constants';
import { clamp } from '@/core/utils/number-util';
import { isSameItem, postCartClientSide } from '@/features/cart/service/cart-service';
import { useCartLocalStorage } from '@/features/cart/service/cart-state';

export type CartItem = {
    cancellation: {
        color: string;
        iconUrl: string;
        overlay: {
            htmlText: string;
            title: string;
        } | null;
        text: string;
    } | null;
    count: {
        current: number;
        max: number;
        min: number;
    };
    endDate: Date;
    offerId: string;
    productId: string;
    productName: string;
    startDate: Date;
    unitPrice: number;
};

type UseCartProps = {
    activitySpotId: number;
    onCartEmpty?: () => void;
    schoolId: number;
};

export default function useCart({ activitySpotId, onCartEmpty, schoolId }: UseCartProps) {
    const { cartStorage, saveCartItems } = useCartLocalStorage({ activitySpotId, schoolId });
    const [cartItems, setCartItems] = React.useState<CartItem[]>([]);
    const [schoolInfo, setSchoolInfo] = React.useState<Cart_jsonld_read['schoolInfo'] | null>(null);
    const [bookingRedirectUrl, setBookingRedirectUrl] = React.useState<null | string>(null);

    const cartRequest = React.useMemo<Cart_jsonld_write>(
        () => ({
            activitySpotId,
            cart: cartStorage.cartItems.map<CartItemRequest>((storageItem) => ({
                count: storageItem.count,
                endDate: storageItem.endDate,
                offerId: storageItem.offerId,
                startDate: storageItem.startDate,
            })),
            schoolId,
        }),
        [activitySpotId, cartStorage, schoolId],
    );

    const queryKey = React.useMemo(() => (cartRequest.cart.length === 0 ? null : ['cart', cartRequest]), [cartRequest]);

    const { data, isLoading } = useQuery('useCart', queryKey, () => postCartClientSide(cartRequest), {
        refetchInterval: DEFAULT_QUERY_STALE_TIME,
        staleTime: DEFAULT_QUERY_STALE_TIME,
    });

    const totalPriceString = React.useMemo(() => {
        const totalCents = cartItems
            ? cartItems.reduce<number>((price, item) => price + item.unitPrice * item.count.current, 0)
            : 0;
        return centsToPriceString(totalCents);
    }, [cartItems]);

    const totalCount = React.useMemo(() => {
        if (!cartItems) {
            return 0;
        }

        return cartItems.reduce<number>((count, item) => count + item.count.current, 0);
    }, [cartItems]);

    const changeItemCount = React.useCallback(
        (cartItems: CartItem[], index: number, delta: number): CartItem[] => {
            const item = cartItems[index];
            if (!item) {
                return cartItems;
            }

            const newCount = clamp(item.count.current + delta, item.count.min, item.count.max);

            if (item.count.current === newCount) {
                return cartItems;
            }

            const newCartItems = cartItems.flatMap((value, valueIndex) => {
                if (valueIndex !== index) {
                    return value;
                }

                if (newCount > 0) {
                    return {
                        ...item,
                        count: {
                            ...item.count,
                            current: newCount,
                        },
                    };
                }

                return [];
            });

            if (newCartItems.length === 0) {
                onCartEmpty?.();
            }

            return newCartItems;
        },
        [onCartEmpty],
    );

    const changeItemCountAndSave = React.useCallback(
        (index: number, delta: number) => {
            const newCartItems = changeItemCount(cartItems, index, delta);
            setCartItems(newCartItems);
            saveCartItems(newCartItems);
        },
        [cartItems, changeItemCount, saveCartItems],
    );

    const addItemAndSave = React.useCallback(
        (newItem: CartItem) => {
            const index = cartItems.findIndex((item) => isSameItem(item, newItem));
            const existingItem = cartItems[index];
            const newCartItems = !existingItem
                ? [...cartItems, newItem]
                : changeItemCount(cartItems, index, newItem.count.current);
            setCartItems(newCartItems);
            saveCartItems(newCartItems);
        },
        [cartItems, changeItemCount, saveCartItems],
    );

    React.useEffect(() => {
        if (!data || data.cart.length === 0) {
            return;
        }

        setBookingRedirectUrl(data.bookingRedirectUrl);
        setSchoolInfo(data.schoolInfo);
        const newCartItems = data.cart.map<CartItem>((responseItem) => {
            return {
                ...responseItem,
                count: {
                    ...responseItem.count,
                    min: responseItem.count.min - 1,
                },
                endDate: new Date(responseItem.endDate),
                startDate: new Date(responseItem.startDate),
            };
        });
        setCartItems(newCartItems);
        saveCartItems(newCartItems);
    }, [data, saveCartItems]);

    return {
        addItem: addItemAndSave,
        bookingRedirectUrl,
        cartItems,
        changeItemCount: changeItemCountAndSave,
        isLoading,
        schoolInfo,
        totalCount,
        totalPriceString,
    };
}
